import { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { isNil } from "lodash";
import { DateTime } from "luxon";
import { usePostHog } from "posthog-js/react";

import { NewTabLink } from "components/NewTabLink";
import { NumberInput } from "components/NumberInput";
import {
  useCarrierShipmentPageContext,
  useShipmentRoute,
  useWriterComponentState,
} from "hooks";
import { MODAL_STYLE } from "layouts/constants";
import {
  formatQuotingUnitOfMeasure,
  formatWeightLb,
  ISOStringToReadableDate,
} from "utils";

import {
  ClientAppEvent,
  EVOTRUX_BLUE,
  SHIPMENT_TRACKING_LINK_BLOG_URL,
} from "../../../../../../constants";

interface Props {
  shipment: CondensedShipment | undefined;
  visible: boolean;
  onClose: VoidFunction;
}

export function BookNowProjectModal(props: Props) {
  const { shipment, visible, onClose } = props;
  const { onRefresh } = useCarrierShipmentPageContext();
  const { route, refetchRoute } = useShipmentRoute(shipment?.info.route);
  const posthog = usePostHog();
  const shipmentRef = useRef<undefined | CondensedShipment>();
  const [canMeetReqs, setCanMeetReqs] = useState<boolean>(false);
  const [trackingLinkOffered, setTrackingLinkOffered] =
    useState<boolean>(false);
  const [userDeliveryEta, setUserDeliveryEta] = useState<string>("");
  const [desiredLoads, setDesiredLoads] = useState<number | null>(1);
  const [notes, setNotes] = useState<string>("");

  const lastRoutePoint = route?.route_points?.length
    ? route.route_points[route.route_points.length - 1]
    : null;
  const isLastRouteASAP = lastRoutePoint?.schedule_type === "ASAP";
  const deliveryEta =
    userDeliveryEta ||
    (!isLastRouteASAP && lastRoutePoint ? lastRoutePoint.start_time : "");

  const deliveryDateTime = DateTime.fromISO(deliveryEta);

  const { write, loading, error } = useWriterComponentState({
    errorMessage:
      "Failed to book project loads. Please contact us if the issue persists.",
    writeConfig: {
      method: "post",
      url: `/shipments/${shipment?.info.id}/projects/book-now`,
      body: {
        desired_loads: desiredLoads,
        delivery_eta: deliveryDateTime,
        tracking_link_offered: trackingLinkOffered,
        notes: notes.trim() || null,
      },
    },
  });

  useEffect(() => {
    if (shipment && shipmentRef.current?.info?.id !== shipment.info.id) {
      shipmentRef.current = shipment;
      refetchRoute();
    }
  }, [shipment, refetchRoute]);

  if (isNil(shipment) || isNil(shipment.project)) {
    return null;
  }

  const { project } = shipment;
  const assignableLoads = project.loads_available - project.loads_booked;
  const loadsCount = desiredLoads ?? 0;
  const isValidLoadsInput =
    !isNil(desiredLoads) && desiredLoads > 0 && desiredLoads <= assignableLoads;
  const pricePerLoad = shipment.book_now_info.book_now_price ?? 0;
  const totalPrice = pricePerLoad * (isValidLoadsInput ? loadsCount : 0);

  const resetBookingModalState = () => {
    setCanMeetReqs(false);
    setDesiredLoads(1);
    setNotes("");
    setTrackingLinkOffered(false);
  };

  const submitBookNow = () => {
    write(() => {
      posthog?.capture(ClientAppEvent.PROJECT_BOOKED_NOW, {
        shipment_id: shipment?.info.id,
        desired_loads_count: desiredLoads,
        has_tracking_link_offer: trackingLinkOffered,
      });
      resetBookingModalState();
      onClose();
      if (onRefresh) {
        onRefresh();
      }
    });
  };

  const getEquipmentTypesDisplay = (equipmentTypes: Equipment[]) => {
    let equipmentTypesDisplay = "";
    equipmentTypes?.forEach((equipmentType, index) => {
      if (index === 0) {
        equipmentTypesDisplay = equipmentType.name;
      } else {
        equipmentTypesDisplay = `${equipmentTypesDisplay}, ${equipmentType.name}`;
      }
    });
    return equipmentTypesDisplay;
  };

  const onModalClose = () => {
    resetBookingModalState();
    onClose();
  };

  return (
    <Modal open={visible} onClose={onModalClose}>
      <Box sx={MODAL_STYLE}>
        <Stack direction="column">
          <Typography variant="h5">
            <b>{`Book loads from this project for $${pricePerLoad} ${shipment.book_now_info.book_now_currency} per load`}</b>{" "}
            <b>
              {shipment.info.quoting_unit_of_measure !== "ALL_IN"
                ? formatQuotingUnitOfMeasure(
                    shipment.info.quoting_unit_of_measure,
                    false,
                  )
                : ""}
            </b>
          </Typography>
          <Stack
            sx={{
              borderColor: EVOTRUX_BLUE,
              borderRadius: 2,
              p: "1rem",
              mt: "1rem",
              mb: "1rem",
            }}
            boxShadow={1}
            direction="column"
          >
            <Typography>
              <b>Available Loads:</b>{" "}
              {`${assignableLoads} of ${project.loads_available}`}
            </Typography>
            <Typography>
              <b>Service:</b> {shipment.info.service}
            </Typography>
            <Typography>
              <b>Weight per load:</b>{" "}
              {formatWeightLb(shipment.info.weight_lb, { includeKg: false })}
            </Typography>
            <Typography>
              <b>Pick up:</b>{" "}
              {ISOStringToReadableDate(shipment.first_point.start_time)}
            </Typography>
            {!isLastRouteASAP && (
              <Typography>
                <b>Deliver:</b>{" "}
                {ISOStringToReadableDate(shipment.last_point.start_time)}
              </Typography>
            )}
            <Typography>
              <b>Equipment:</b> {getEquipmentTypesDisplay(shipment.equipments)}
            </Typography>
            <Typography>
              <b>Special Requirements:</b>{" "}
              {shipment.info?.special_requirements ?? ""}
            </Typography>
            <Typography>
              <b>Product Dimensions:</b>{" "}
              {shipment.info?.additional_details ?? ""}
            </Typography>
          </Stack>
          <NumberInput
            /*
            No min/max: NumberInput's underlying base-ui NumberField clamps a
            typed value into that range rather than keeping it, which would
            silently turn a request for more loads than are assignable into a
            booking for the assignable count and make the message below
            unreachable.
            */
            inputLabel="How many loads do you want to book?"
            value={desiredLoads}
            integerOnly
            onChange={setDesiredLoads}
          />
          {!isValidLoadsInput && (
            <Typography color="error" variant="body2" mt="0.5rem">
              {loadsCount > assignableLoads
                ? `Only ${assignableLoads} loads available`
                : "Please enter a valid number of loads (greater than 0)"}
            </Typography>
          )}
          {shipment.info.quoting_unit_of_measure === "ALL_IN" && (
            <Typography variant="h6" mt="1rem">
              <b>Total Price:</b>{" "}
              {`$${totalPrice.toFixed(2)} ${shipment.book_now_info.book_now_currency}`}
            </Typography>
          )}
          {isLastRouteASAP && (
            <TextField
              id="datetime-local"
              label="Delivery ETA"
              type="date"
              value={deliveryEta}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => setUserDeliveryEta(e.target.value)}
              required
              sx={{ mt: "1rem" }}
            />
          )}
          <TextField
            id="notes"
            label="Notes (optional)"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional details about your quote (e.g., fuel surcharge, special equipment, etc.)"
            inputProps={{ maxLength: 1024 }}
            helperText={`${notes.length}/1024 characters`}
            sx={{ mt: "1rem", width: "100%" }}
          />
          <Stack direction="row" mt="1rem" alignItems="center">
            <Checkbox
              checked={trackingLinkOffered}
              onChange={() => setTrackingLinkOffered(!trackingLinkOffered)}
            />
            <span>
              {"I can provide a tracking link for this shipment. "}
              <NewTabLink href={SHIPMENT_TRACKING_LINK_BLOG_URL}>
                <p>What are tracking links?</p>
              </NewTabLink>
            </span>
          </Stack>
          <Stack direction="row" alignItems="center" mt="1rem" mb="1rem">
            <Checkbox
              required
              checked={canMeetReqs}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setCanMeetReqs(event.target.checked)
              }
            />
            <Typography variant="h6" color="black">
              I confirm I will pick up these shipments with the requested
              equipments and can meet all service requirements listed above.
            </Typography>
          </Stack>
          {error !== null && (
            <Typography color="red" align="center">
              {error}
            </Typography>
          )}
          {!loading && (
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="success"
                disabled={
                  !canMeetReqs ||
                  !isValidLoadsInput ||
                  (isLastRouteASAP && deliveryEta.length === 0)
                }
                onClick={submitBookNow}
              >
                {`BOOK ${loadsCount} LOAD${loadsCount > 1 ? "S" : ""} NOW`}
              </Button>
              <Button variant="outlined" onClick={onModalClose}>
                cancel
              </Button>
            </Stack>
          )}
          {loading && (
            <Stack>
              <CircularProgress />
            </Stack>
          )}
        </Stack>
      </Box>
    </Modal>
  );
}
